import { Button } from "../ui/Button"
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/Select"
import { Atom, atom, useAtom, useAtomValue } from "jotai"
import {
	BitDepth,
	DimensionSide,
	DimensionType,
	ExportImageOptions,
	ExportImageType,
	FilenameType,
	exportBitDepthAtom,
	exportColorSpaceAtom,
	exportDimensionAtom,
	exportDimensionSideAtom,
	exportDimensionTypeAtom,
	exportImageTypeAtom,
	exportPixelPerInchAtom,
	exportQualityAtom,
	filenameNumberAtom,
	filenamePrefixAtom,
	filenameTypeAtom,
	publishBitDepthAtom,
	publishColorSpaceAtom,
	publishDimensionAtom,
	publishDimensionSideAtom,
	publishDimensionTypeAtom,
	publishImageTypeAtom,
	publishPixelPerInchAtom,
	publishQualityAtom,
	publishWatermarkAtom,
	selectedOptionAtom,
} from "@/core/export-image"
import { Dialog } from "@/components/ui/Dialog"
import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import { pushDialog, resetDialogs } from "@/store/dialogs-state"
import {
	cancelExportAtom,
	cancelExportFromDialog,
	exportPhotos,
	exportProgressAtom,
	getExportedFilename,
} from "@/store/editor/photo-exporting"
import {
	exportablePhotosAtom,
	displayGroupPhotosCountAtom,
	metadataAtom,
	selectedPhotosCountAtom,
	selectedExportablePhotosCountAtom,
	selectedExportablePhotosAtom,
} from "@/store/photo/photos-state"
import { getFileForPhoto } from "@/store/files-state"
import { useUserQuery } from "@/api/users-api"
import { clamp } from "@/utils/math"
import toast from "react-hot-toast"
import { projectAtom } from "@/store/project/projects-state"
import { parseInteger, toNumOrNull } from "@/utils/number"
import { ProgressBar } from "../ui/ProgressBar"
import { currentStyleAtom } from "@/store/style/style-state"
import { isAbortError } from "@/utils/error"
import { Sentry } from "@/telemetry/sentry"
import { chooseAndValidateDirectory } from "@/storage/open-directory"
import { isZipfileIOEnabled } from "@/utils/zipfile-io"
import { watermarksAtom } from "@/store/Pixieset/PixiesetService"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { collectionAtom } from "@/store/Pixieset/store-client-gallery"
import { WatermarkResponseDTO } from "@/api/PSApiOperations"
import { psGalleriesBaseUri } from "@/api/constants"
import { ExportScopeSelector } from "./ExportScopeSelector"
import { runExportPublishPreflight } from "./SubscriptionWarningDialogs"
import { getExportResizeOptions } from "@/core/export-resize-options"
import { type ValidatedExportImageType } from "@/core/utils/encoded-size-check"
import { exportImageTypeKeys } from "@/i18n/dual-role-strings"
import { useEstimatedExportSize } from "./useEstimatedExportSize"

export type ExportType = "export" | "publish"

const makeConditional =
	<T,>(base: Atom<T>, fallback: T) =>
	(enabled: boolean) =>
		atom((get) => (enabled ? get(base) : fallback))

const makeConditionalQuery = <T extends { data?: any }>(
	base: Atom<T>,
	fallbackData: NonNullable<T["data"]>,
) => {
	return (enabled: boolean) =>
		atom((get) => {
			if (enabled) return get(base)
			return { data: fallbackData } as T
		})
}

const mapExportImageTypeToImageType = (
	exportImageType: ExportImageType,
): ValidatedExportImageType => {
	const map: Record<ExportImageType, ValidatedExportImageType> = {
		JPEG: "jpg",
		PSD: "psd",
		TIFF: "tif",
	}

	return map[exportImageType]
}

export const ExportOptionsView = (props: { type: ExportType }) => {
	const { t } = useTranslation("components")
	const [imageType, setImageType] = useAtom(
		props.type === "export" ? exportImageTypeAtom : publishImageTypeAtom,
	)
	const [dimensionType, setDimensionType] = useAtom(
		props.type === "export" ? exportDimensionTypeAtom : publishDimensionTypeAtom,
	)
	const [dimensionSide, setDimensionSide] = useAtom(
		props.type === "export" ? exportDimensionSideAtom : publishDimensionSideAtom,
	)
	const [dimension, setDimension] = useAtom(
		props.type === "export" ? exportDimensionAtom : publishDimensionAtom,
	)
	const [bitDepth, setBitDepth] = useAtom(
		props.type === "export" ? exportBitDepthAtom : publishBitDepthAtom,
	)
	const [quality, setQuality] = useAtom(
		props.type === "export" ? exportQualityAtom : publishQualityAtom,
	)
	const [colorSpace, setColorSpace] = useAtom(
		props.type === "export" ? exportColorSpaceAtom : publishColorSpaceAtom,
	)
	const [pixelPerInch, setPixelPerInch] = useAtom(
		props.type === "export" ? exportPixelPerInchAtom : publishPixelPerInchAtom,
	)

	const conditionalCollection = makeConditional(collectionAtom, null as any)
	const conditionalWatermarks = makeConditionalQuery(watermarksAtom, [] as WatermarkResponseDTO[])
	const collection = useAtomValue(
		useMemo(() => conditionalCollection(props.type === "publish"), [props.type]),
	)
	const watermarks = useAtomValue(
		useMemo(() => conditionalWatermarks(props.type === "publish"), [props.type, collection]),
	).data
	const [watermark, setWatermark] = useAtom(publishWatermarkAtom)

	useEffect(() => {
		const newValue = toNumOrNull(collection?.["watermark_id"])
		setWatermark((prev) => (prev !== newValue ? newValue : prev))
	}, [collection?.["watermark_id"], setWatermark])

	const renderImageType = () => {
		return (
			<Select
				trackingContext="export_options"
				trackingLabel="image_type"
				aria-label={t("exportDialog.ariaSelectImageFormat")}
				value={imageType}
				disabled={props.type === "publish"}
				onValueChange={(value) => setImageType(value as ExportImageType)}
			>
				<SelectTrigger>
					<div data-testid="export_image_type_select" className="mr-2 text-base-400">
						{t("exportDialog.imageType")}
					</div>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{(["JPEG", "TIFF", "PSD"] as const).map((option) => (
							<SelectItem key={option} value={option}>
								<span data-testid={`export_image_type_select_option_${option}`}>
									{t(exportImageTypeKeys[option])}
								</span>
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		)
	}

	const renderBitDepth = () => {
		return (
			<Select
				trackingContext="export_options"
				trackingLabel="bit_depth"
				aria-label={t("exportDialog.ariaSelectBitDepth")}
				value={bitDepth.toString()}
				onValueChange={(value) => setBitDepth(Number(value) as BitDepth)}
			>
				<SelectTrigger>
					<div className="mr-2 text-base-400">{t("exportDialog.bitDepth")}</div>
					<div>{t("exportDialog.bit", { bitDepth: bitDepth })}</div>
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{([16, 8] as const).map((option) => (
							<SelectItem key={option} value={option.toString()}>
								{t("exportDialog.bit2", { option: option })}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		)
	}

	const renderColorSpace = () => {
		const colorSpaces: Record<PredefinedColorSpace, string> = {
			"display-p3": t("exportDialog.colorSpaceDisplayP3"),
			srgb: "sRGB", // i18n-ignore
		}

		return (
			<Select
				trackingContext="export_options"
				trackingLabel="color_space"
				aria-label={t("exportDialog.ariaSelectColorSpace")}
				value={colorSpace}
				onValueChange={(value) => setColorSpace(value as PredefinedColorSpace)}
			>
				<SelectTrigger>
					<div className="mr-2 text-base-400">{t("exportDialog.colorSpace")}</div>
					<div>{colorSpaces[colorSpace]}</div>
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{Object.entries(colorSpaces).map(([colorSpace, name]) => (
							<SelectItem key={colorSpace} value={colorSpace}>
								{name}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		)
	}

	const renderPixelsPerInch = () => {
		return (
			<div className="flex flex-row items-center space-x-1 py-1">
				<div className="mr-1 text-sm text-base-400">{t("exportDialog.resolution")}</div>
				<input
					className="h-6 w-10 rounded text-center text-xs"
					type="text"
					value={pixelPerInch}
					onChange={(e) => {
						setPixelPerInch(parseInteger(e.target.value))
					}}
				/>
				<div className="text-xs">{t("exportDialog.ppiUnit")}</div>
			</div>
		)
	}

	const allWatermarks: { name: string; id: number | null }[] = useMemo(() => {
		if (!watermarks?.length) return [{ name: t("exportDialog.noWatermark"), id: null }]
		return [
			{ name: t("exportDialog.noWatermark"), id: null },
			...watermarks.map((w) => ({ name: w.name, id: w.id })),
		]
	}, [watermarks])

	const renderWatermark = () => {
		if (allWatermarks.length < 2) {
			return (
				<div className="flex w-full flex-row items-center justify-between pt-2">
					<div className="mr-2 text-base-400">{t("exportDialog.watermark")}</div>
					<div
						className="text-base-300 hover:text-base-50"
						onClick={() => {
							window.open(`${psGalleriesBaseUri}/settings/watermark`, "_blank")
						}}
					>
						{t("exportDialog.setupInClientGallery")}
					</div>
				</div>
			)
		}

		const index = allWatermarks.findIndex((item) => item.id === watermark)
		const validIndex = index >= 0 ? index : 0

		const watermarkName = allWatermarks[validIndex]?.name ?? t("exportDialog.noWatermark")

		return (
			<Select
				trackingContext="export_options"
				trackingLabel="watermark"
				value={String(watermark)}
				onValueChange={(value) => {
					const watermarkId = toNumOrNull(value)
					setWatermark(watermarkId)
				}}
			>
				<SelectTrigger>
					<div className="mr-2 text-base-400">{t("exportDialog.watermark")}</div>
					<div>{watermarkName}</div>
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{allWatermarks.map((option) => (
							<SelectItem key={option.id} value={String(option.id)}>
								{option.name}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		)
	}

	const renderDimensionType = () => {
		return (
			<Select
				trackingContext="export_options"
				trackingLabel="dimension_type"
				aria-label={t("exportDialog.ariaSelectDimensionType")}
				value={dimensionType}
				onValueChange={(value) => setDimensionType(value as DimensionType)}
			>
				<SelectTrigger>
					<div className="mr-2 text-base-400">{t("exportDialog.dimensions")}</div>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{(["Full size", "Custom"] as const).map(
							// i18n-ignore: DimensionType data keys
							(
								option, // i18n-ignore
							) => (
								<SelectItem key={option} value={option}>
									{option === "Full size"
										? t("exportDialog.dimensionFullSize")
										: t("exportDialog.dimensionCustom")}
								</SelectItem>
							),
						)}
					</SelectGroup>
				</SelectContent>
			</Select>
		)
	}

	const renderDimensionSide = () => {
		return (
			<Select
				trackingContext="export_options"
				trackingLabel="dimension_side"
				aria-label={t("exportDialog.ariaSelectDimensionSide")}
				value={dimensionSide}
				onValueChange={(value) => setDimensionSide(value as DimensionSide)}
			>
				<SelectTrigger>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{(["Long side", "Short side"] as const).map(
							// i18n-ignore: DimensionSide data keys
							(
								option, // i18n-ignore
							) => (
								<SelectItem key={option} value={option}>
									{option === "Long side"
										? t("exportDialog.dimensionLongSide")
										: t("exportDialog.dimensionShortSide")}
								</SelectItem>
							),
						)}
					</SelectGroup>
				</SelectContent>
			</Select>
		)
	}

	const renderDimension = () => {
		return (
			<div className="flex flex-row items-center space-x-1">
				<input
					className="h-6 w-10 rounded text-center text-xs"
					type="text"
					maxLength={5}
					value={dimension}
					onChange={(e) => {
						setDimension(parseInteger(e.target.value))
					}}
				/>
				<div className="text-xs">{t("exportDialog.pxUnit")}</div>
			</div>
		)
	}

	const renderQuality = () => {
		const displayedQuality = `${Math.round(quality * 100)}%`

		const parseQuality = (quality: string) => {
			return clamp(parseFloat(quality) ?? 0, 10, 100) / 100
		}

		const qualities = ["100%", "90%", "80%", "70%", "60%", "50%"] as const

		return (
			<Select
				trackingContext="export_options"
				trackingLabel="quality"
				value={displayedQuality}
				onValueChange={(value) => setQuality(parseQuality(value))}
			>
				<SelectTrigger>
					<div className="mr-2 text-base-400">{t("exportDialog.quality")}</div>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{qualities.map((option) => (
							<SelectItem key={option} value={option}>
								{option}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		)
	}

	return (
		<div className="grid w-full grid-cols-2 items-start justify-between space-x-3">
			<div className="flex w-fit flex-col items-start justify-start space-y-0">
				{renderImageType()}
				{imageType === "JPEG" && renderQuality()}
				{(imageType === "TIFF" || imageType === "PSD") && renderBitDepth()}
				{renderColorSpace()}
			</div>

			<div className="flex w-fit flex-col items-start justify-start space-y-0">
				{renderDimensionType()}
				{dimensionType == "Custom" && (
					<div className="flex flex-row items-center space-x-0">
						{renderDimensionSide()}
						{renderDimension()}
					</div>
				)}
				{renderPixelsPerInch()}
				{props.type === "publish" && renderWatermark()}
			</div>
		</div>
	)
}

export const ExportDialog = () => {
	const { t } = useTranslation("components")
	const [filenameType, setFilenameType] = useAtom(filenameTypeAtom)
	const [filenamePrefix, setFilenamePrefix] = useAtom(filenamePrefixAtom)
	const [filenameNumber, setFilenameNumber] = useAtom(filenameNumberAtom)
	const [selectedOption, setSelectedOption] = useAtom(selectedOptionAtom)
	const imageType = useAtomValue(exportImageTypeAtom)
	const dimensionType = useAtomValue(exportDimensionTypeAtom)
	const dimensionSide = useAtomValue(exportDimensionSideAtom)
	const dimension = useAtomValue(exportDimensionAtom)
	const bitDepth = useAtomValue(exportBitDepthAtom)
	const quality = useAtomValue(exportQualityAtom)
	const colorSpace = useAtomValue(exportColorSpaceAtom)
	const pixelPerInch = useAtomValue(exportPixelPerInchAtom)

	const [project] = useAtom(projectAtom)
	const [photos] = useAtom(exportablePhotosAtom)
	const metadata = useAtomValue(metadataAtom)
	const selectedCount = useAtomValue(selectedPhotosCountAtom)
	const selectedExportableCount = useAtomValue(selectedExportablePhotosCountAtom)
	const [selectedPhotos] = useAtom(selectedExportablePhotosAtom)
	const [{ processing: processingCount }] = useAtom(displayGroupPhotosCountAtom)
	const isProcessing = processingCount > 0
	const [currentStyle] = useAtom(currentStyleAtom)

	const userQuery = useUserQuery(false)

	const filenameTypeDisplayString = (type: FilenameType) => {
		switch (type) {
			case "original":
				return t("exportDialog.filenameOriginal")
			case "custom-sequence":
				return t("exportDialog.filenameCustomSequence")
			case "custom-originalNumber":
				return t("exportDialog.filenameCustomOriginalNumber")
			case "date":
				return t("exportDialog.filenameDateFilename")
		}
	}

	const filenameExample = (type?: FilenameType) => {
		const photo = selectedPhotos.at(0) ?? photos.at(0)

		if (!photo) {
			return null
		}

		const file = getFileForPhoto(photo)
		if (file) {
			return getExportedFilename({
				photo,
				filenameType: type ?? filenameType,
				fileExtension: mapExportImageTypeToImageType(imageType),
				filenamePrefix: filenamePrefix,
				number: filenameNumber,
			})
		}
	}

	const renderFilenameType = () => {
		return (
			<Select
				trackingContext="export_dialog"
				trackingLabel="filename_type"
				aria-label={t("exportDialog.ariaSelectFilenamePattern")}
				value={filenameType}
				onValueChange={(value) => setFilenameType(value as FilenameType)}
			>
				<SelectTrigger>
					<div data-testid="export_filename_select" className="mr-2 text-base-400">
						{t("exportDialog.fileName")}
					</div>
					<div>{filenameTypeDisplayString(filenameType)}</div>
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{(
							[
								"original",
								"custom-sequence",
								"custom-originalNumber",
								"date",
							] as const
						).map((option) => (
							<SelectItem
								key={option}
								value={option}
								disabled={filenameExample(option) === null}
							>
								<span data-testid={`export_filename_select_option_${option}`}>
									{filenameTypeDisplayString(option as FilenameType)}
								</span>
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		)
	}

	const renderFilenameSection = () => {
		if (filenameType === "date" || filenameType === "original") {
			return (
				<div className="flex flex-col items-start">
					{renderFilenameType()}
					{renderFilenameExample()}
				</div>
			)
		} else if (filenameType === "custom-originalNumber") {
			return (
				<div className="flex w-full flex-col items-center">
					{renderFilenameType()}
					<div className="grid w-full grid-cols-2 items-center justify-between space-x-4">
						{renderFilenameExample()}
						{renderFilenamePrefix()}
					</div>
				</div>
			)
		} else if (filenameType === "custom-sequence") {
			return (
				<div className="grid w-full grid-cols-2 items-start justify-between space-x-4">
					<div className="flex w-fit flex-col items-start justify-start space-y-0">
						{renderFilenameType()}
						{renderFilenameExample()}
					</div>
					<div className="flex flex-col items-start">
						{renderFilenamePrefix()}
						{renderFilenameNumber()}
					</div>
				</div>
			)
		}
	}
	const renderFilenameExample = () => {
		return (
			<div className="flex flex-row space-x-2 py-2">
				<div className="text-sm text-base-400">{t("exportDialog.example")}</div>
				<div className="text-sm text-base-400">{filenameExample()}</div>
			</div>
		)
	}

	const renderFilenamePrefix = () => {
		return (
			<div className="flex h-8 flex-row items-center space-x-2">
				<div className="text-sm">{t("exportDialog.customName")}</div>
				<input
					className="h-6 w-24 rounded pl-2 text-start text-xs"
					type="text"
					value={filenamePrefix}
					data-testid={`export_filename_custom_input`}
					onChange={(e) => {
						setFilenamePrefix(e.target.value.replace(/[^a-zA-Z0-9._ -]+/, ""))
					}}
				/>
			</div>
		)
	}

	const renderFilenameNumber = () => {
		return (
			<div className="flex h-8 flex-row items-center space-x-3">
				<div className="text-sm">{t("exportDialog.startNumber")}</div>
				<input
					className="h-6 w-10 rounded pl-2 text-start text-xs"
					type="text"
					value={filenameNumber}
					onChange={(e) => {
						setFilenameNumber(parseInteger(e.target.value))
					}}
				/>
			</div>
		)
	}

	const getExportOptions = () => {
		let options: ExportImageOptions = {
			projectId: project?.id,
			type: mapExportImageTypeToImageType(imageType),
			bitDepth: imageType === "TIFF" || imageType === "PSD" ? bitDepth : undefined,
			filenameType: filenameType,
			filenamePrefix: filenamePrefix,
			filenameNumber: filenameNumber,
			quality,
			colorSpace,
			ppi: pixelPerInch,
			format: imageType === "JPEG" ? "yuv" : "rgb-planar",
		}

		if (dimensionType == "Custom") {
			options = {
				...options,
				...getExportResizeOptions(dimensionType, dimensionSide, dimension),
			}
		}
		return options
	}

	const getAnalyticsValue = () => {
		const options = getExportOptions()
		return JSON.stringify({ ...options, photoCount, style: currentStyle?.name ?? "No Style" }) // i18n-ignore
	}

	const photoCount = selectedOption == "selected" ? selectedExportableCount : photos.length
	const photosToEstimate = selectedOption == "selected" ? selectedPhotos : photos
	const hasTooSmallCustomDimension = dimensionType === "Custom" && dimension < 1
	const estimatedExportSize = useEstimatedExportSize({
		projectId: project?.id,
		photos: photosToEstimate,
		metadata,
		format: mapExportImageTypeToImageType(imageType),
		quality,
		bitDepth: imageType === "JPEG" ? undefined : bitDepth,
		colorSpace,
		dimensionType,
		dimensionSide,
		dimension,
	})
	const customDimensionError = hasTooSmallCustomDimension
		? t("exportDialog.customDimensionError")
		: estimatedExportSize.hasUnsupportedCustomDimensions
			? t("exportDialog.customDimensionError2")
			: null

	const confirmExport = async () => {
		resetDialogs()

		const canProceed = await runExportPublishPreflight({
			photoCount,
			origin: "export menu",
			refetchUser: () => userQuery.refetch(),
			skipCreditCheck: project?.isSampleProject === true,
		})
		if (!canProceed) {
			return
		}

		const useZip = isZipfileIOEnabled()
		const directory = useZip
			? await (
					await navigator.storage.getDirectory()
				).getDirectoryHandle("exports", {
					create: true,
				})
			: await chooseAndValidateDirectory("export_photo")
		if (!(directory instanceof FileSystemDirectoryHandle)) return

		try {
			await exportPhotos(
				selectedOption == "selected" ? selectedPhotos : photos,
				directory,
				getExportOptions(),
			)
		} catch (e) {
			if (isAbortError(e)) {
				return
			}

			toast.error(t("exportDialog.failedToExportPleaseTry"))
			Sentry.captureException(e)
		}
	}

	return (
		<Dialog trackingViewName="export_dialog" contentSize="sm-h-auto" disableClose>
			<div className="flex w-full flex-col items-start justify-start space-y-4 text-sm">
				<div className="text-start text-md font-bold">{t("exportDialog.title")}</div>
				<ExportOptionsView type="export" />

				<div className="flex h-px w-full bg-base-700"></div>
				{renderFilenameSection()}

				<div className="flex h-px w-full bg-base-700"></div>
				<ExportScopeSelector
					groupName="export-scope"
					selectedOption={selectedOption}
					setSelectedOption={setSelectedOption}
					selectedCount={selectedCount}
					selectedExportableCount={selectedExportableCount}
					totalCount={photos.length}
					isProcessing={isProcessing}
				/>
				<div className="text-sm text-base-400">
					{t("exportDialog.estimatedExportSize")}&nbsp;
					<span className="text-base-50">{estimatedExportSize.text}</span>
				</div>
				{customDimensionError && (
					<div className="text-start text-xs text-red-400">{customDimensionError}</div>
				)}
				<div className="flex w-full flex-1 flex-row items-center justify-end space-x-2">
					<Button
						trackingLabel="cancel_export"
						aria-label={t("exportDialog.ariaCancelPhotoExport")}
						onClick={() => {
							resetDialogs()
						}}
					>
						{t("common:action.cancel")}
					</Button>

					<Button
						trackingLabel="confirm_export"
						aria-label={t("exportDialog.confirmExportPhoto", {
							photoCount: photoCount,
							var1: photoCount !== 1 ? "s" : "",
						})}
						trackingValue={getAnalyticsValue()}
						onClick={() => {
							void confirmExport()
						}}
						variant="primary"
						disabled={
							photoCount < 1 ||
							customDimensionError !== null ||
							(isProcessing && selectedOption === "all")
						}
					>
						{t("exportDialog.exportImageCount", { count: photoCount })}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}

export const ExportLoadingDialog = () => {
	const { t } = useTranslation("components")
	const isCanceling = useAtomValue(cancelExportAtom)
	const { totalProgress, total, saves, totalToSave } = useAtomValue(exportProgressAtom)

	const cancel = () => {
		//TODO: pause export while user confirms
		pushDialog(ConfirmDialog, {
			title: t("exportDialog.cancelExportTitle"),
			message: t("exportDialog.cancelExportMessage"),
			primary: {
				title: t("exportDialog.cancelExportPrimary"),
				isDestructive: true,
				handler: cancelExportFromDialog,
			},
			secondary: { title: t("common:action.back") },
			trackingViewName: "cancel_export_confirmation",
		})
	}

	return (
		<Dialog trackingViewName="export_loading_dialog" disableClose>
			<div className="flex w-[462px] flex-col space-y-3">
				<div className="flex flex-row items-center justify-between">
					<div className="text-md font-bold">
						{t("exportDialog.exportingProgress", { saves, totalToSave })}
					</div>
				</div>

				<ProgressBar max={total} value={totalProgress} />

				<div className="text-xs">
					{isCanceling
						? t("exportDialog.cancellingExport")
						: t("exportDialog.doNotLeaveApp")}
				</div>

				<div className="self-end pt-3">
					<Button
						trackingLabel="cancel_export"
						aria-label={t("exportDialog.ariaCancelPhotoExport")}
						onClick={cancel}
						disabled={isCanceling}
					>
						{t("common:action.cancel")}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}

export type ExportExistingFilesResult = "overwrite" | "skip" | "cancel"
export interface ExportExistingFilesProp {
	existingFiles: string[]
	resultHandler: (result: ExportExistingFilesResult) => void
}

export const ExportExistingFilesDialog = (props: ExportExistingFilesProp) => {
	const { t } = useTranslation("components")
	const fileCount = props.existingFiles.length
	const fileOrFiles = fileCount > 1 ? "files" : "file"

	return (
		<Dialog trackingViewName="export_existing_files_dialog" disableClose>
			<div className="flex w-[462px] flex-col space-y-3">
				<div className="text-start text-md font-bold">
					{t("exportDialog.existingFound", {
						fileCount: fileCount,
						fileOrFiles: fileOrFiles,
					})}
				</div>
				<div className="text-start text-sm">
					{t("exportDialog.doYouWantToOverwrite", {
						fileOrFiles: fileOrFiles,
					})}
				</div>

				<div className="no-scrollbar flex h-[100px] w-full flex-col justify-start overflow-auto">
					{props.existingFiles.map((filename) => (
						<div className="text-xs text-base-400" key={filename}>
							{filename}
						</div>
					))}
				</div>

				<div className="flex flex-row justify-end space-x-2">
					<Button
						trackingLabel="overwrite"
						onClick={() => props.resultHandler("overwrite")}
					>
						{t("exportDialog.overwrite")}
					</Button>
					<Button trackingLabel="skip" onClick={() => props.resultHandler("skip")}>
						{t("common:action.skip")}
					</Button>
					<Button trackingLabel="cancel" onClick={() => props.resultHandler("cancel")}>
						{t("common:action.cancel")}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
